// ⌘K / Ctrl+K quick switcher, like Discord's: type to filter channels and DMs, ↑/↓ to move, Enter to go, Esc to
// close. With nothing typed, channels with unread mentions come first, then other unread ones.
import { useEffect, useMemo, useRef, useState } from "react";
import type { Channel } from "@bloom/shared";
import { switcherScore } from "../chatsLogic.ts";

export interface SwitcherItem {
  channel: Pick<Channel, "id" | "kind"> & { serverId?: string; name?: string };
  /** Channel name, or the other people's names for a DM. */
  label: string;
  /** Category name, or "Direct message", or server name. */
  group?: string;
  unread: boolean;
  mentions: number;
}

interface Props {
  /** In sidebar order. */
  items: SwitcherItem[];
  currentId: string | undefined;
  onPick: (channelId: string, serverId?: string) => void;
  onClose: () => void;
  /** Picking where something goes (a share from another app, share.ts): its heading ("Send to…") and what's sent. */
  sendTo?: { heading: string; what: string };
}

export function QuickSwitcher({ items, currentId, onPick, onClose, sendTo }: Props) {
  const [q, setQ] = useState("");
  const [index, setIndex] = useState(0);
  const list = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    const query = q.trim().toLowerCase().replace(/^[#@]/, "");
    const ranked = items
      .map((it, order) => ({ it, order, s: query ? switcherScore(it.label, query) : 0 }))
      .filter((r): r is { it: SwitcherItem; order: number; s: number } => r.s !== null);
    // Unread (mentions first) ahead of read; the open channel last when nothing is typed; then sidebar order.
    const rank = (r: (typeof ranked)[number]) => [r.s, r.it.mentions > 0 ? 0 : r.it.unread ? 1 : 2, !query && r.it.channel.id === currentId ? 1 : 0, r.order];
    return ranked
      .sort((a, b) => {
        const ra = rank(a);
        const rb = rank(b);
        for (let i = 0; i < ra.length; i++) if (ra[i] !== rb[i]) return ra[i]! - rb[i]!;
        return 0;
      })
      .map((r) => r.it)
      .slice(0, 50);
  }, [items, q, currentId]);

  useEffect(() => setIndex(0), [q]);
  useEffect(() => {
    list.current?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({ block: "nearest" });
  }, [index]);

  const go = (i: number) => {
    const it = results[i];
    if (it) onPick(it.channel.id, it.channel.serverId);
  };

  return (
    <div className="overlay quick-switcher" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="switcher" role="dialog" aria-modal="true" aria-label={sendTo ? sendTo.heading : "Quick switcher"}>
        {sendTo && (
          <div className="switcher-send">
            <h2>{sendTo.heading}</h2>
            <p title={sendTo.what}>{sendTo.what}</p>
          </div>
        )}
        <input
          className="switcher-input"
          autoFocus
          placeholder={sendTo ? "Search channels and DMs" : "Where would you like to go?"}
          aria-label="Search channels and DMs"
          role="combobox"
          aria-expanded="true"
          aria-controls="switcher-list"
          aria-activedescendant={results[index] ? `switcher-${results[index]!.channel.id}` : undefined}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              const n = results.length || 1;
              setIndex((i) => (i + (e.key === "ArrowDown" ? 1 : n - 1)) % n);
            } else if (e.key === "Enter" && !e.nativeEvent.isComposing) {
              e.preventDefault();
              go(index);
            } else if (e.key === "Escape") {
              e.preventDefault();
              onClose();
            }
          }}
        />
        <div className="switcher-list" id="switcher-list" role="listbox" ref={list}>
          {results.length === 0 && <div className="switcher-empty">{q.trim() ? `No channels match “${q}”` : "Loading conversations…"}</div>}
          {results.map((it, i) => (
            <button
              key={`${it.channel.serverId ?? ""}/${it.channel.id}`}
              id={`switcher-${it.channel.id}`}
              type="button"
              role="option"
              aria-selected={i === index}
              data-index={i}
              className={`switcher-item${i === index ? " active" : ""}${it.unread ? " unread" : ""}`}
              onMouseMove={() => i !== index && setIndex(i)}
              onClick={() => go(i)}
            >
              <span className="switcher-glyph" aria-hidden="true">{it.channel.kind === "dm" ? "@" : "#"}</span>
              <span className="switcher-name">{it.label}</span>
              {it.group && <span className="switcher-group">{it.group}</span>}
              {it.mentions > 0 ? (
                <span className="mention-pill" aria-label={`${it.mentions} mentions`}>{it.mentions > 99 ? "99+" : it.mentions}</span>
              ) : (
                it.unread && <span className="unread-dot switcher-dot" aria-label="unread" />
              )}
            </button>
          ))}
        </div>
        <div className="switcher-hint" aria-hidden="true">
          <span><kbd>↑</kbd><kbd>↓</kbd> to move</span>
          <span><kbd>↵</kbd> to go</span>
          <span><kbd>esc</kbd> to close</span>
          <span className="switcher-hint-more"><kbd>alt</kbd>+<kbd>↑</kbd><kbd>↓</kbd> channels · add <kbd>shift</kbd> for unread</span>
        </div>
      </div>
    </div>
  );
}
